// src/pages/OpportunitiesPage.js
import { useState, useEffect } from 'react';

import { useAuth } from '../context/AuthContext';
import { getOpportunities, postOpportunity } from '../api';
import NavBar from '../components/shared/NavBar';

const NAVY  = '#1B3A6B';
const GOLD  = '#C9A84C';
const RED   = '#c0392b';
const GREEN = '#1a6b3a';

const CATEGORIES = ['Jobs', 'Business', 'Events', 'Housing', 'Other'];

const CATEGORY_STYLES = {
  Jobs:     { bg: '#d4edda', color: GREEN,    icon: '💼' },
  Business: { bg: '#d6eaf8', color: NAVY,     icon: '🤝' },
  Events:   { bg: '#fff3cd', color: '#856404', icon: '🎉' },
  Housing:  { bg: '#fdecea', color: RED,       icon: '🏠' },
  Other:    { bg: '#f0f0f0', color: '#555',    icon: '📌' }
};

export default function OpportunitiesPage() {
  const { member, role } = useAuth();

  const [opportunities, setOpportunities] = useState([]);
  const [filtered,      setFiltered]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState('');
  const [success,       setSuccess]       = useState('');
  const [showForm,      setShowForm]      = useState(false);
  const [posting,       setPosting]       = useState(false);
  const [expanded,      setExpanded]      = useState(null);

  // Filters
  const [search,         setSearch]         = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  // Form
  const [form, setForm] = useState({
    title:    '',
    body:     '',
    category: 'Jobs'
  });

  useEffect(() => {
    loadOpportunities();
  }, []);

  useEffect(() => {
    let list = [...opportunities];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(o =>
        o.title?.toLowerCase().includes(q) ||
        o.body?.toLowerCase().includes(q) ||
        o.category?.toLowerCase().includes(q)
      );
    }
    if (categoryFilter) {
      list = list.filter(o => o.category === categoryFilter);
    }
    setFiltered(list);
  }, [search, categoryFilter, opportunities]);

  async function loadOpportunities() {
    setLoading(true);
    try {
      const result = await getOpportunities();
      setOpportunities(result.opportunities || []);
      setFiltered(result.opportunities || []);
    } catch (err) {
      setError('Could not load opportunities: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  const handlePost = async () => {
    setError('');
    if (!form.title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!form.body.trim()) {
      setError('Details are required.');
      return;
    }

    setPosting(true);
    try {
      await postOpportunity(form);
      setSuccess('Opportunity posted successfully!');
      setForm({ title: '', body: '', category: 'Jobs' });
      setShowForm(false);
      await loadOpportunities();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to post: ' + err.message);
    } finally {
      setPosting(false);
    }
  };


  const formatDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      const now  = new Date();
      const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
      if (diff === 0) return 'Today';
      if (diff === 1) return 'Yesterday';
      if (diff < 7)  return `${diff} days ago`;
      return date.toLocaleDateString('en-GB', {
        day: 'numeric', month: 'short', year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const isOwnPost = (postedBy) => postedBy === member?.email;
  const canRemove = (postedBy) =>
    isOwnPost(postedBy) || role === 'executive';

  // Category counts
  const categoryCounts = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = opportunities.filter(o => o.category === cat).length;
    return acc;
  }, {});

  return (
    <div style={{
      minHeight: '100vh', background: '#f0f4f8',
      fontFamily: 'Arial, sans-serif'
    }}>

      {/* Nav */}
      <NavBar />

      <div style={{
        maxWidth: '900px', margin: '0 auto',
        padding: '24px 16px'
      }}>

        {/* Page Header */}
        <div style={{
          background: `linear-gradient(135deg, ${NAVY} 0%, #2d5a9e 100%)`,
          borderRadius: '12px', padding: '24px',
          marginBottom: '20px', color: '#fff',
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', flexWrap: 'wrap', gap: '12px'
        }}>
          <div>
            <h2 style={{ margin: '0 0 4px', fontSize: '22px' }}>
              🤝 Opportunities Board
            </h2>
            <p style={{ margin: 0, opacity: 0.85, fontSize: '14px' }}>
              {loading ? 'Loading...'
                : `${opportunities.length} opportunity${opportunities.length !== 1 ? 's' : ''} posted`}
            </p>
          </div>
          <button
            onClick={() => {
              setShowForm(!showForm);
              setError('');
            }}
            style={{
              background: GOLD, color: NAVY,
              border: 'none', borderRadius: '8px',
              padding: '10px 20px', cursor: 'pointer',
              fontWeight: 'bold', fontSize: '14px'
            }}
          >
            {showForm ? '✕ Cancel' : '+ Post Opportunity'}
          </button>
        </div>

        {/* Category Summary */}
        <div style={{
          display: 'flex', gap: '8px',
          marginBottom: '20px', flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setCategoryFilter('')}
            style={{
              padding: '8px 16px',
              background: !categoryFilter ? NAVY : '#fff',
              color: !categoryFilter ? '#fff' : '#555',
              border: `2px solid ${!categoryFilter
                ? NAVY : '#e0e0e0'}`,
              borderRadius: '20px', cursor: 'pointer',
              fontSize: '13px', fontWeight: '600'
            }}
          >
            All ({opportunities.length})
          </button>
          {CATEGORIES.map(cat => {
            const style = CATEGORY_STYLES[cat];
            const count = categoryCounts[cat];
            const active = categoryFilter === cat;
            return (
              <button
                key={cat}
                onClick={() => setCategoryFilter(
                  categoryFilter === cat ? '' : cat
                )}
                style={{
                  padding: '8px 16px',
                  background: active ? style.color : style.bg,
                  color: active ? '#fff' : style.color,
                  border: `2px solid ${style.color}`,
                  borderRadius: '20px', cursor: 'pointer',
                  fontSize: '13px', fontWeight: '600'
                }}
              >
                {style.icon} {cat} ({count})
              </button>
            );
          })}
        </div>

        {/* Success */}
        {success && (
          <div style={{
            background: '#d4edda', border: '1px solid #c3e6cb',
            borderRadius: '8px', padding: '12px 16px',
            marginBottom: '16px', color: GREEN,
            fontSize: '14px', fontWeight: '600'
          }}>
            ✓ {success}
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            background: '#fdecea', border: '1px solid #f5c6cb',
            borderRadius: '8px', padding: '12px 16px',
            marginBottom: '16px', color: RED, fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Post Form */}
        {showForm && (
          <div style={{
            background: '#fff', borderRadius: '12px',
            padding: '24px', marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: `2px solid ${GOLD}`
          }}>
            <h3 style={{
              color: NAVY, margin: '0 0 20px', fontSize: '16px'
            }}>
              Post an Opportunity
            </h3>

            <div style={{
              display: 'flex', flexDirection: 'column', gap: '16px'
            }}>

              {/* Category */}
              <div>
                <label style={{
                  display: 'block', fontSize: '13px',
                  fontWeight: '600', color: '#555',
                  marginBottom: '8px'
                }}>
                  Category
                </label>
                <div style={{
                  display: 'flex', gap: '8px', flexWrap: 'wrap'
                }}>
                  {CATEGORIES.map(cat => {
                    const s = CATEGORY_STYLES[cat];
                    const active = form.category === cat;
                    return (
                      <button
                        key={cat}
                        onClick={() => setForm(p => ({
                          ...p, category: cat
                        }))}
                        style={{
                          padding: '7px 14px',
                          background: active ? s.color : s.bg,
                          color: active ? '#fff' : s.color,
                          border: `2px solid ${s.color}`,
                          borderRadius: '20px', cursor: 'pointer',
                          fontSize: '13px', fontWeight: '600'
                        }}
                      >
                        {s.icon} {cat}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Title */}
              <div>
                <label style={{
                  display: 'block', fontSize: '13px',
                  fontWeight: '600', color: '#555',
                  marginBottom: '6px'
                }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(p => ({
                    ...p, title: e.target.value
                  }))}
                  placeholder="e.g. Software Engineer needed at ABC Company"
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px', fontSize: '14px',
                    outline: 'none', boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Details */}
              <div>
                <label style={{
                  display: 'block', fontSize: '13px',
                  fontWeight: '600', color: '#555',
                  marginBottom: '6px'
                }}>
                  Details *
                </label>
                <textarea
                  value={form.body}
                  onChange={e => setForm(p => ({
                    ...p, body: e.target.value
                  }))}
                  placeholder="Provide full details — requirements, contact info, deadlines..."
                  rows={5}
                  style={{
                    width: '100%', padding: '10px 14px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px', fontSize: '14px',
                    outline: 'none', boxSizing: 'border-box',
                    resize: 'vertical',
                    fontFamily: 'Arial, sans-serif'
                  }}
                />
              </div>

              {/* Submit */}
              <button
                onClick={handlePost}
                disabled={posting}
                style={{
                  padding: '12px 24px',
                  background: posting ? '#ccc' : NAVY,
                  color: '#fff', border: 'none',
                  borderRadius: '8px',
                  cursor: posting ? 'not-allowed' : 'pointer',
                  fontSize: '15px', fontWeight: 'bold',
                  alignSelf: 'flex-end'
                }}
              >
                {posting ? 'Posting...' : 'Post Opportunity'}
              </button>
            </div>
          </div>
        )}

        {/* Search */}
        <div style={{
          background: '#fff', borderRadius: '12px',
          padding: '12px 16px', marginBottom: '16px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
        }}>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="🔍 Search opportunities..."
            style={{
              width: '100%', padding: '10px 14px',
              border: '2px solid #e0e0e0', borderRadius: '8px',
              fontSize: '14px', outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>

        {/* Opportunities List */}
        {loading ? (
          <div style={{
            textAlign: 'center', padding: '60px',
            color: '#666', fontSize: '14px'
          }}>
            Loading opportunities...
          </div>
        ) : filtered.length === 0 ? (
          <div style={{
            background: '#fff', borderRadius: '12px',
            padding: '60px', textAlign: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              🤝
            </div>
            <h3 style={{ color: NAVY, margin: '0 0 8px' }}>
              {search || categoryFilter
                ? 'No opportunities match your search'
                : 'No opportunities yet'}
            </h3>
            <p style={{ color: '#666', margin: '0 0 16px' }}>
              {search || categoryFilter
                ? 'Try adjusting your search or category filter.'
                : 'Be the first to post an opportunity!'}
            </p>
            {(search || categoryFilter) && (
              <button
                onClick={() => {
                  setSearch('');
                  setCategoryFilter('');
                }}
                style={{
                  background: NAVY, color: '#fff',
                  border: 'none', borderRadius: '8px',
                  padding: '10px 24px', cursor: 'pointer',
                  fontSize: '14px', fontWeight: 'bold'
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <div style={{
            display: 'flex', flexDirection: 'column', gap: '16px'
          }}>
            {filtered.map(opp => {
              const catStyle = CATEGORY_STYLES[opp.category]
                || CATEGORY_STYLES.Other;
              const isExp = expanded === opp.id;
              const mine  = isOwnPost(opp.postedBy);

              return (
                <div
                  key={opp.id}
                  style={{
                    background: '#fff', borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    overflow: 'hidden',
                    border: mine
                      ? `2px solid ${GOLD}`
                      : '2px solid transparent'
                  }}
                >
                  {/* Card Header */}
                  <div
                    onClick={() =>
                      setExpanded(isExp ? null : opp.id)
                    }
                    style={{
                      padding: '20px 24px', cursor: 'pointer'
                    }}
                  >
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      alignItems: 'flex-start', gap: '12px',
                      flexWrap: 'wrap'
                    }}>
                      <div style={{ flex: 1 }}>
                        {/* Category badge */}
                        <span style={{
                          background: catStyle.bg,
                          color: catStyle.color,
                          padding: '3px 10px', borderRadius: '10px',
                          fontSize: '12px', fontWeight: 'bold',
                          marginBottom: '8px',
                          display: 'inline-block'
                        }}>
                          {catStyle.icon} {opp.category}
                        </span>
                        {mine && (
                          <span style={{
                            background: '#fff3cd', color: '#856404',
                            padding: '3px 10px', borderRadius: '10px',
                            fontSize: '12px', fontWeight: 'bold',
                            marginBottom: '8px', marginLeft: '6px',
                            display: 'inline-block'
                          }}>
                            Your post
                          </span>
                        )}

                        {/* Title */}
                        <h3 style={{
                          margin: '8px 0 6px',
                          color: NAVY, fontSize: '16px',
                          fontWeight: 'bold'
                        }}>
                          {opp.title}
                        </h3>

                        {/* Meta */}
                        <div style={{
                          fontSize: '12px', color: '#888',
                          display: 'flex', gap: '12px',
                          flexWrap: 'wrap'
                        }}>
                          <span>📅 {formatDate(opp.date)}</span>
                          <span>✉️ {opp.postedBy}</span>
                        </div>
                      </div>

                      {/* Expand arrow */}
                      <div style={{
                        color: '#aaa', fontSize: '18px',
                        flexShrink: 0
                      }}>
                        {isExp ? '▲' : '▼'}
                      </div>
                    </div>

                    {/* Preview when collapsed */}
                    {!isExp && (
                      <p style={{
                        margin: '10px 0 0', fontSize: '14px',
                        color: '#666', overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }}>
                        {opp.body}
                      </p>
                    )}
                  </div>

                  {/* Expanded content */}
                  {isExp && (
                    <div style={{
                      padding: '0 24px 20px',
                      borderTop: '1px solid #f0f0f0'
                    }}>
                      <div style={{
                        paddingTop: '16px',
                        fontSize: '15px', color: '#333',
                        lineHeight: '1.7', whiteSpace: 'pre-wrap'
                      }}>
                        {opp.body}
                      </div>

                      {/* Actions */}
                      <div style={{
                        marginTop: '16px', paddingTop: '12px',
                        borderTop: '1px solid #f0f0f0',
                        display: 'flex', gap: '8px',
                        justifyContent: 'flex-end'
                      }}>
                        {canRemove(opp.postedBy) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Mark as removed — future enhancement
                              setSuccess(
                                'Contact an executive to remove posts.'
                              );
                            }}
                            style={{
                              padding: '6px 14px',
                              background: '#fdecea', color: RED,
                              border: `2px solid ${RED}`,
                              borderRadius: '6px', cursor: 'pointer',
                              fontSize: '12px', fontWeight: '600'
                            }}
                          >
                            🗑 Remove
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}